import React from 'react';
import { useTranslation } from 'react-i18next';
import SVGImage from '../layout/SVGImage';
import { format } from 'date-fns';

interface PowerOutageScheduleTableProps {
  data: Array<PowerOutageScheduleData>;
  className?: string;
  testId?: string;
  handleDelete?: (startTime: string, endTime: string) => void;
  handleUpdate?: (
    startTime: string,
    endTime: string,
    utilRateGenerators: Array<{ number: number; value: number }>
  ) => void;
}

export type PowerOutageScheduleData = {
  date: string;
  capacity: string;
  utilization?: string;
  lastUpdated: string;
  target: string;
  memo?: string;
  active: boolean;
  startTime: string;
  endTime: string;
  utilRateGenerators: Array<{ number: number; value: number }>;
};

const PowerOutageScheduleTable: React.FC<PowerOutageScheduleTableProps> = ({
  data,
  className,
  testId,
  handleDelete,
  handleUpdate,
}) => {
  const { t } = useTranslation();

  const headers = [
    t('overview.schedule.date'),
    t('overview.schedule.capacity'),
    t('overview.schedule.utilization'),
    t('overview.schedule.target'),
    t('overview.schedule.lastUpdated'),
    t('overview.schedule.note'),
    t('overview.schedule.setting'),
  ];

  return (
    <table data-testid={testId} className={`${className} w-full`}>
      <thead>
        <tr className="h-[36px] rounded-[5px] bg-[#1F384C] text-white">
          {headers.map((value, index) =>
            index === 0 ? (
              <th
                key={index}
                className="pop-normal-3 rounded-bl-[5px] rounded-tl-[5px]"
              >
                {value}
              </th>
            ) : index === headers.length - 1 ? (
              <th
                key={index}
                className="pop-normal-3 rounded-br-[5px] rounded-tr-[5px]"
              >
                {value}
              </th>
            ) : (
              <th key={index} className="pop-normal-3">
                {value}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {data.map((value, index) => (
          <tr key={index} data-testid={`${testId}-item`}>
            {Object.keys(value).map((key) =>
              key === 'active' ||
              key === 'startTime' ||
              key === 'endTime' ||
              key === 'utilRateGenerators' ||
              key === 'key' ? (
                <React.Fragment key={key + index}></React.Fragment>
              ) : (
                <td
                  className={`pop-13-normal border-b border-[#E2E7E7] py-[13px] text-center leading-none text-dark-6 ${value.active ? 'bg-white' : 'bg-light-3'}`}
                  key={key + index}
                  data-testid={`${testId}-item-td`}
                >
                  {key === 'lastUpdated' && value[key]
                    ? format(value[key], 'yyyy-MM-dd HH:mm:ss')
                    : key === 'memo'
                      ? value[key]?.slice(0, 10)
                      : value[
                          key as keyof Omit<
                            PowerOutageScheduleData,
                            | 'active'
                            | 'startTime'
                            | 'endTime'
                            | 'utilRateGenerators'
                            | 'key'
                          >
                        ]}
                </td>
              )
            )}
            <td
              className={`pop-13-normal flex justify-center border-b border-[#E2E7E7] py-[13px] text-center leading-none text-dark-6 ${value.active ? 'bg-white' : 'bg-light-3'}`}
            >
              <SVGImage
                name="edit"
                className="mr-[14px] cursor-pointer"
                testId={`${testId}-setting-update`}
                onClick={() =>
                  value.active
                    ? handleUpdate?.(
                        value.startTime,
                        value.endTime,
                        value.utilRateGenerators
                      )
                    : null
                }
              />
              <SVGImage
                name="delete"
                className="cursor-pointer"
                onClick={() =>
                  value.active
                    ? handleDelete?.(value.startTime, value.endTime)
                    : null
                }
                testId={`${testId}-setting-delete`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default PowerOutageScheduleTable;
